// router/index.js
import { auth } from "@/firebase"; // getAuth() ではなく、初期化済みの auth を使う
import { createRouter, createWebHistory } from "vue-router";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { useAuthStore } from "@/stores/authStore";
import DefaultLayout from "@/layouts/DefaultLayout.vue";
import LandingLayout from "@/layouts/LandingLayout.vue";
import { useRoomStore } from "@/stores/roomStore"; // ルームストアをインポート
import { bizhubRoute } from "./bizhub";

// 権限のある最初の生成タブ（無ければ null）
function firstGenerateRoute() {
  const s = useAuthStore().userSettings;
  if (s.can_access_text2image) return "GenerateImage";
  if (s.can_access_text2video) return "GenerateVideo";
  if (s.can_access_text2model || s.can_access_image2model) return "GenerateModel";
  return null;
}

const routes = [
  // ✅ ランディングページ（未ログイン用）
  {
    path: "/",
    component: LandingLayout,
    children: [
      {
        path: "",
        name: "Landing",
        component: () => import("@/views/LandingPage.vue"),
        meta: { isLanding: true },
      },
    ],
  },

  // ✅ TaiTockランディングページ（未ログイン用）
  {
    path: "/taitock",
    component: LandingLayout,
    children: [
      {
        path: "",
        name: "TaitockLanding",
        component: () => import("@/views/TaitockLandingPage.vue"),
        meta: { isLanding: true, isTaitock: true },
      },
    ],
  },

  // ✅ Qumowill AI マニュアル ランディングページ（未ログイン用）
  {
    path: "/qumowill",
    component: LandingLayout,
    children: [
      {
        path: "",
        name: "QumowillLanding",
        component: () => import("@/views/QumowillLandingPage.vue"),
        meta: { isLanding: true, isQumowill: true },
      },
    ],
  },

  // ✅ ログイン後のメインレイアウト
  {
    path: "/app",
    component: DefaultLayout,
    children: [
      {
        path: "rooms",
        name: "RoomList",
        component: () => import("@/components/RoomList/RoomList.vue"),
      },
      {
        path: "rooms/:roomId",
        name: "RoomDetail",
        component: () => import("@/components/RoomList/RoomDetailPage.vue"),
        props: true,
      },
      {
        path: "files",
        name: "FileList",
        component: () => import("@/components/FileList/FileList.vue"),
      },
      {
        path: "admin",
        name: "Admin",
        component: () => import("@/views/Admin/Admin.vue"),
        meta: { requiresAuth: true, adminOnly: true },
      },
      {
        path: "log",
        name: "AdminLog",
        component: () => import("@/views/Admin/AdminLog.vue"),
        meta: { requiresAuth: true, adminOnly: true },
      },
      {
        path: "account",
        name: "AccountDetail",
        component: () => import("@/views/AccountDetail.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "generate",
        component: () => import("@/views/Generate/GenerateHubView.vue"),
        meta: { requiresAuth: true, requiresAnyGeneration: true },
        children: [
          { path: "", name: "GenerateHub", redirect: { name: "GenerateImage" } },
          {
            path: "image",
            name: "GenerateImage",
            component: () => import("@/views/Generate/GenerateImagePanel.vue"),
            meta: { requiresAuth: true, requiresText2Image: true, generateTab: "image" },
          },
          {
            path: "video",
            name: "GenerateVideo",
            component: () => import("@/views/Generate/VideoGenPanel.vue"),
            meta: { requiresAuth: true, requiresText2Video: true, generateTab: "video" },
          },
          {
            path: "model",
            name: "GenerateModel",
            component: () => import("@/views/Generate/GenerateModelPanel.vue"),
            meta: { requiresAuth: true, requiresAnyModelGen: true, generateTab: "model" },
          },
        ],
      },
      { path: "text2image", redirect: { name: "GenerateImage" } },
      { path: "text2model", redirect: { name: "GenerateModel", query: { input: "text" } } },
      { path: "image2model", redirect: { name: "GenerateModel", query: { input: "image" } } },
      {
        path: "manual",
        name: "ManualList",
        component: () => import("@/views/Manual/ManualList.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "manual/:manualId",
        name: "ManualDetail",
        component: () => import("@/views/Manual/ManualDetail.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: "manual/:manualId/video",
        name: "ManualVideo",
        component: () => import("@/views/Manual/ManualVideo.vue"),
        props: true,
        meta: { requiresAuth: true, hideAppBarOnMobile: true },
      },
      {
        path: "manual/:manualId/document",
        name: "ManualDocument",
        component: () => import("@/views/Manual/ManualDocument.vue"),
        props: true,
        meta: { requiresAuth: true },
      },
      {
        path: "downloads",
        name: "Downloads",
        component: () => import("@/views/DownloadPage.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "operation",
        name: "OperationView",
        component: () => import("@/views/OperationView.vue"),
        meta: { requiresAuth: true, requiresOperation: true },
      },
      {
        path: "operation/edit",
        name: "OperationTemplateEdit",
        component: () => import("@/views/OperationTemplateEdit.vue"),
        meta: { requiresAuth: true, requiresOperation: true },
      },
      {
        path: "chat",
        name: "ChatView",
        component: () => import("@/views/ChatView.vue"),
        meta: { requiresAuth: true, requiresChat: true },
      },
      {
        path: "analyzer",
        name: "AnalyzerView",
        component: () => import("@/views/AnalyzerView.vue"),
        meta: { requiresAuth: true, requiresChat: true },
      },
      {
        path: "dig",
        name: "DigView",
        component: () => import("@/views/Dig/DigView.vue"),
        meta: { requiresAuth: true },
      },
      {
        path: "spaces",
        name: "SpacesView",
        component: () => import("@/views/Spaces/SpacesView.vue"),
        meta: { requiresAuth: true, requiresSpaces: true },
      },
      {
        path: "spaces/building/:frameId",
        name: "SpaceBuildingView",
        component: () => import("@/views/Spaces/SpaceBuildingView.vue"),
        meta: { requiresAuth: true, requiresSpaces: true },
      },
      {
        path: "spaces/:id",
        name: "SpaceDetailView",
        component: () => import("@/views/Spaces/SpaceDetailView.vue"),
        meta: { requiresAuth: true, requiresSpaces: true },
      },
      {
        path: "okaki",
        name: "OkakiListView",
        component: () => import("@/views/Okaki/OkakiListView.vue"),
        meta: { requiresAuth: true, requiresOkaki: true },
      },
      {
        path: "okaki/:id",
        name: "OkakiDetailView",
        component: () => import("@/views/Okaki/OkakiDetailView.vue"),
        meta: { requiresAuth: true, requiresOkaki: true },
      },
      {
        path: "slide-videos",
        name: "SlideVideoList",
        component: () => import("@/views/SlideVideo/SlideVideoList.vue"),
        meta: { requiresAuth: true, requiresSlideVideo: true },
      },
      {
        path: "slide-videos/:id",
        name: "SlideVideoEditor",
        component: () => import("@/views/SlideVideo/SlideVideoEditor.vue"),
        meta: { requiresAuth: true, requiresSlideVideo: true },
      },
      bizhubRoute,
      {
        path: "ai-npc",
        name: "AiNpcView",
        component: () => import("@/views/AiNpc/AiNpcView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/personalities/new",
        name: "AiNpcPersonalityCreate",
        component: () => import("@/views/AiNpc/PersonalityEdit.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/personalities/:id",
        name: "AiNpcPersonalityEdit",
        component: () => import("@/views/AiNpc/PersonalityEdit.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/scenarios",
        name: "AiNpcScenarioList",
        component: () => import("@/views/AiNpc/ScenarioListView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/scenarios/new",
        name: "AiNpcScenarioCreate",
        component: () => import("@/views/AiNpc/ScenarioEditView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/scenarios/:id",
        name: "AiNpcScenarioEdit",
        component: () => import("@/views/AiNpc/ScenarioEditView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/conversation-logs",
        name: "AiNpcConversationLogList",
        component: () => import("@/views/AiNpc/ConversationLogListView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/conversation-logs/:id",
        name: "AiNpcConversationLogDetail",
        component: () => import("@/views/AiNpc/ConversationLogDetailView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/knowledge",
        name: "AiNpcKnowledgeList",
        component: () => import("@/views/AiNpc/KnowledgeListView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/knowledge/new",
        name: "AiNpcKnowledgeCreate",
        component: () => import("@/views/AiNpc/KnowledgeEditView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/knowledge/:id",
        name: "AiNpcKnowledgeEdit",
        component: () => import("@/views/AiNpc/KnowledgeEditView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "ai-npc/avatars",
        name: "AiNpcAvatarList",
        component: () => import("@/views/AiNpc/AvatarListView.vue"),
        meta: { requiresAuth: true, requiresAiNpc: true },
      },
      {
        path: "dig/data-sources",
        name: "DigDataSources",
        component: () => import("@/views/Dig/DataSourcesPage.vue"),
        meta: { requiresAuth: true },
      },
    ],
  },

  // ✅ 旧URLからの互換性リダイレクト（/rooms → /app/rooms など）
  { path: "/rooms", redirect: "/app/rooms" },
  { path: "/rooms/:roomId", redirect: to => `/app/rooms/${to.params.roomId}` },
  { path: "/files", redirect: "/app/files" },
  { path: "/admin", redirect: "/app/admin" },
  { path: "/log", redirect: "/app/log" },
  { path: "/account", redirect: "/app/account" },
  { path: "/text2image", redirect: "/app/text2image" },
  { path: "/text2model", redirect: "/app/text2model" },
  { path: "/image2model", redirect: "/app/image2model" },
  { path: "/chat", redirect: "/app/chat" },
  { path: "/analyzer", redirect: "/app/analyzer" },
  { path: "/manual", redirect: "/app/manual" },
  { path: "/manual/:manualId", redirect: to => `/app/manual/${to.params.manualId}` },
  { path: "/operation", redirect: "/app/operation" },
  { path: "/downloads", redirect: "/app/downloads" },
  { path: "/dig", redirect: "/app/dig" },
  { path: "/ai-npc", redirect: "/app/ai-npc" },

  // ✅ 公開ルーム（認証不要）
  {
    path: "/public/rooms/:roomNumber",
    name: "PublicRoom",
    component: () => import("@/components/RoomList/RoomDetailPage.vue"),
    meta: { publicView: true },
  },

  // ✅ 法的ページ（認証不要）
  {
    path: "/terms",
    name: "TermsOfService",
    component: () => import("@/views/Legal/TermsOfService.vue"),
    meta: { skipAuth: true },
  },
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: () => import("@/views/Legal/PrivacyPolicy.vue"),
    meta: { skipAuth: true },
  },

  // ✅ 公開マニュアル（認証不要）
  {
    path: "/public/manual/:publicToken",
    name: "PublicManualDetail",
    component: () => import("@/views/Manual/PublicManualDetail.vue"),
    props: true,
    meta: { publicView: true },
  },
  {
    path: "/public/manual/:publicToken/video",
    name: "PublicManualVideo",
    component: () => import("@/views/Manual/PublicManualVideo.vue"),
    props: true,
    meta: { publicView: true },
  },
  {
    path: "/public/manual/:publicToken/document",
    name: "PublicManualDocument",
    component: () => import("@/views/Manual/PublicManualDocument.vue"),
    props: true,
    meta: { publicView: true },
  },

  // ✅ モバイル用ファイルビューページ
  {
    path: "/file/:fileId",
    name: "file-view",
    component: () => import("@/components/FileList/FileViewPage.vue"),
    props: true,
  },

  {
    path: "/anchor",
    name: "AnchorView",
    component: () => import("@/views/AnchorViewer.vue"),
  },
  {
    path: "/anchorqr",
    name: "AnchorQrView",
    component: () => import("@/views/AnchorQrViewer.vue"),
  },

  {
    path: "/gallery",
    name: "GalleryView",
    component: () => import("@/gallery/GalleryView.vue"),
  },
  {
    path: "/gallery/:id",
    name: "GalleryDetail",
    component: () => import("@/gallery/GalleryDetailView.vue"),
    props: true,
  },
  {
    path: "/public/gallery/:id",
    name: "PublicGalleryDetail",
    component: () => import("@/gallery/GalleryDetailView.vue"),
    props: true,
    meta: { publicView: true },
  },
  {
    path: "/rooms/:roomId/movie",
    name: "RoomMovie",
    redirect: to => `/app/rooms/${to.params.roomId}/movie`,
  },
  {
    path: "/app/rooms/:roomId/movie",
    name: "AppRoomMovie",
    component: () => import("@/views/MoviePlayerView.vue"),
  },

  {
    path: "/rooms/:roomId/fileplayer",
    name: "RoomFilePlayer",
    redirect: to => `/app/rooms/${to.params.roomId}/fileplayer`,
  },
  {
    path: "/app/rooms/:roomId/fileplayer",
    name: "AppRoomFilePlayer",
    component: () => import("@/views/FilePlayerView.vue"),
  },

  // ログインページ（COEP無効で独立）
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginPage.vue"),
    meta: { skipAuth: true },
  },

  // 404など
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // ブラウザの戻る/進むボタンの場合は保存された位置を復元
    if (savedPosition) {
      return savedPosition;
    }
    // それ以外のページ遷移はトップにスクロール
    return { top: 0 };
  },
});

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore();

  console.log("[Router] Navigating to:", to.path, "from:", from.path, "meta:", to.meta);

  // ✅ ルームからルーム一覧に戻るときは選択状態をクリア
  if (from.path.startsWith("/app/rooms/") && to.path === "/app/rooms") {
    const roomStore = useRoomStore();
    roomStore.setSelectedRoom(null);
  }

  // ✅ skipAuth または publicView の場合は認証チェックをスキップ
  if (to.meta.skipAuth || to.meta.publicView) {
    return next();
  }

  // ✅ ランディングページの場合
  if (to.meta.isLanding) {
    // Firebaseの認証状態を確認
    const user = await new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });

    // ログイン済み（ゲスト以外）の場合はホーム画面へリダイレクト
    if (user && !user.email?.startsWith("guest@")) {
      authStore.setUser(user);
      await authStore.loadUserSettings();

      if (authStore.userSettings.is_admin) {
        return next("/app/admin");
      }

      if (['taitockeye', 'qumowill'].includes(authStore.userSettings.user_type)) {
        return next("/app/manual");
      }
      return next("/app/rooms");
    }

    // 未ログインまたはゲストの場合はランディングページを表示
    return next();
  }

  // ✅ /app 以下のページは認証が必要
  if (to.path.startsWith("/app")) {
    // Firebaseの認証状態を確認
    let user = auth.currentUser;

    if (!user) {
      user = await new Promise((resolve) => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
          unsubscribe();
          resolve(user);
        });
      });
    }

    // ログイン済み（ゲスト含む）の場合
    if (user) {
      authStore.setUser(user);
      // userSettingsが未ロードの場合はロード（user_typeがデフォルト値'normal'でも権限が未設定の場合がある）
      if (!authStore.userSettings._loaded) {
        await authStore.loadUserSettings();
      }

      // 🔐 adminOnly ルートに管理者以外がアクセスした場合
      if (to.meta.adminOnly && !authStore.userSettings.is_admin) {
        return next("/app/rooms");
      }

      // 🔐 AI 生成ハブ: 4 フラグのどれかが必要
      if (to.meta.requiresAnyGeneration && !authStore.canAccessAnyGeneration) {
        console.warn("[Router] generation access denied - redirecting to home");
        return next(authStore.homePath);
      }
      const generateFallback = () => {
        const name = firstGenerateRoute();
        return name && name !== to.name ? next({ name }) : next(authStore.homePath);
      };
      if (to.meta.requiresText2Video && !authStore.userSettings.can_access_text2video) {
        return generateFallback();
      }
      if (to.meta.requiresAnyModelGen && !(authStore.userSettings.can_access_text2model || authStore.userSettings.can_access_image2model)) {
        return generateFallback();
      }

      // 🔐 Text2Image権限チェック
      if (to.meta.requiresText2Image && !authStore.userSettings.can_access_text2image) {
        console.warn("[Router] Text2Image access denied - redirecting to generate fallback");
        return generateFallback();
      }

      // 🔐 Text2Model権限チェック
      if (to.meta.requiresText2Model && !authStore.userSettings.can_access_text2model) {
        console.warn("[Router] Text2Model access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 Image2Model権限チェック
      if (to.meta.requiresImage2Model && !authStore.userSettings.can_access_image2model) {
        console.warn("[Router] Image2Model access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 Chat権限チェック
      if (to.meta.requiresChat && !authStore.userSettings.can_access_chat) {
        console.warn("[Router] Chat access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 AI NPC権限チェック
      if (to.meta.requiresAiNpc && !authStore.userSettings.can_access_ai_npc) {
        console.warn("[Router] AI NPC access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 オペレーション権限チェック
      if (to.meta.requiresOperation && !authStore.userSettings.can_access_operation) {
        console.warn("[Router] Operation access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 Spaces 権限チェック
      if (to.meta.requiresSpaces && !authStore.userSettings.can_access_spaces) {
        console.warn("[Router] Spaces access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 Okaki 権限チェック
      if (to.meta.requiresOkaki && !authStore.userSettings.can_access_okaki) {
        console.warn("[Router] Okaki access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 業務管理（bizhub）権限チェック
      if (to.meta.requiresBizhub && !authStore.userSettings.can_access_bizhub) {
        console.warn("[Router] Bizhub access denied - redirecting to home");
        return next(authStore.homePath);
      }

      // 🔐 スライド動画 権限チェック
      if (to.meta.requiresSlideVideo && !authStore.userSettings.can_access_slide_video) {
        console.warn("[Router] SlideVideo access denied - redirecting to home");
        return next(authStore.homePath);
      }

      return next();
    }

    // 未ログインの場合はランディングページへ
    return next("/");
  }

  next();
});

export default router;
